import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import {
  AIAssistantSettingsApi,
  AiAssistantSettings,
  PatchAiAssistantSettingsModeEnum,
} from '@tourfold/libraries/tf-api-client-public-v2';
import {
  resolveSettingsLoadErrorDetails,
  resolveSettingsProblemMessage,
  SettingsActions,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsInlineMessageContent,
  type SettingsLoadErrorDetails,
  SETTINGS_PROBLEM_TRANSLATION_KEYS,
  SETTINGS_SUBPROBLEM_TRANSLATION_KEYS,
} from '@tourfold/features/settings-ui';
import { from } from 'rxjs';
import { ResourceAccessService } from '@tourfold/shared-kernels/resource-access';
import { FrontendConfigurationService } from '@tourfold/shared-kernels/frontend-configuration';

interface ModeOption {
  label: string;
  value: PatchAiAssistantSettingsModeEnum;
}

const AI_ASSISTANT_SETTINGS_FIELD_LABEL_KEYS: Readonly<Record<string, string>> = {
  mode: 'AI_ASSISTANT.SETTINGS.ERROR.FIELD_MODE',
};

@Component({
  selector: 'tf-settings-page-ai-assistant',
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    SelectModule,
    SettingsActions,
    SettingsInlineMessage,
    SettingsLoadError,
  ],
  templateUrl: './ai-assistant-settings.component.html',
  styleUrl: './ai-assistant-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAssistantSettingsComponent implements OnInit {
  private readonly aiAssistantSettingsApi = inject(AIAssistantSettingsApi);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly resourceAccessService = inject(ResourceAccessService);
  private readonly tenantConfigService = inject(FrontendConfigurationService);

  readonly canUpdateAiAssistant = computed(() =>
    this.resourceAccessService.canPerform('aiAssistantSettings', 'update')
  );

  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly settings = signal<AiAssistantSettings | null>(null);
  readonly hasLoadedOnce = signal(false);
  readonly initialLoadFailed = signal(false);
  readonly loadError = signal<SettingsInlineMessageContent | null>(null);
  readonly loadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly saveError = signal<SettingsInlineMessageContent | null>(null);
  readonly selectedMode = signal<PatchAiAssistantSettingsModeEnum>(
    PatchAiAssistantSettingsModeEnum.Off
  );

  readonly modeOptions = signal<ModeOption[]>([]);

  ngOnInit(): void {
    this.initModeOptions();
    this.loadSettings();
  }

  private initModeOptions(): void {
    this.modeOptions.set([
      {
        label: this.translateService.instant('AI_ASSISTANT.SETTINGS.MODE_OFF'),
        value: PatchAiAssistantSettingsModeEnum.Off,
      },
      {
        label: this.translateService.instant('AI_ASSISTANT.SETTINGS.MODE_READ_ONLY'),
        value: PatchAiAssistantSettingsModeEnum.ReadOnly,
      },
      {
        label: this.translateService.instant('AI_ASSISTANT.SETTINGS.MODE_READ_WRITE'),
        value: PatchAiAssistantSettingsModeEnum.ReadWrite,
      },
    ]);
  }

  private loadSettings(): void {
    const hadLoadedOnce = this.hasLoadedOnce();
    this.isLoading.set(true);
    this.saveError.set(null);
    from(this.aiAssistantSettingsApi.getAiAssistantSettings())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (settings) => {
          this.settings.set(settings);
          this.selectedMode.set(settings.mode);
          this.loadError.set(null);
          this.loadErrorDetails.set(null);
          this.initialLoadFailed.set(false);
          this.hasLoadedOnce.set(true);
          this.isLoading.set(false);
        },
        error: (error: unknown) => {
          void this.handleLoadError(error, hadLoadedOnce);
        },
      });
  }

  onModeChange(mode: PatchAiAssistantSettingsModeEnum | null): void {
    if (mode) {
      this.selectedMode.set(mode);
      this.saveError.set(null);
    }
  }

  cancel(): void {
    this.loadSettings();
  }

  retryLoad(): void {
    this.loadSettings();
  }

  clearSaveError(): void {
    this.saveError.set(null);
  }

  save(): void {
    if (!this.canUpdateAiAssistant() || !this.settings()) {
      return;
    }

    this.isSaving.set(true);
    this.saveError.set(null);
    from(
      this.aiAssistantSettingsApi.patchAiAssistantSettings({
        patchAiAssistantSettings: { mode: this.selectedMode() },
      })
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (settings) => {
          this.settings.set(settings);
          this.selectedMode.set(settings.mode);
          this.loadError.set(null);
          this.saveError.set(null);
          this.tenantConfigService.refreshAuthenticatedConfiguration();
          this.isSaving.set(false);
        },
        error: (error: unknown) => {
          void this.handleSaveError(error);
        },
      });
  }

  private async handleSaveError(error: unknown): Promise<void> {
    this.saveError.set(
      await this.resolveErrorMessage(error, 'AI_ASSISTANT.SETTINGS.ERROR.SAVE_FAILED')
    );
    this.isSaving.set(false);
  }

  private async handleLoadError(error: unknown, hadLoadedOnce: boolean): Promise<void> {
    const [message, details] = await Promise.all([
      this.resolveErrorMessage(error, 'AI_ASSISTANT.SETTINGS.ERROR.LOAD_FAILED'),
      resolveSettingsLoadErrorDetails(error),
    ]);
    if (hadLoadedOnce) {
      this.saveError.set(message);
      this.loadError.set(null);
      this.loadErrorDetails.set(null);
      this.initialLoadFailed.set(false);
    } else {
      this.loadError.set(message);
      this.loadErrorDetails.set(details);
      this.initialLoadFailed.set(true);
    }
    this.isLoading.set(false);
  }

  private async resolveErrorMessage(
    error: unknown,
    fallbackKey: string
  ): Promise<SettingsInlineMessageContent> {
    return resolveSettingsProblemMessage(error, this.translateService, {
      fallbackKey,
      problemTranslationKeys: SETTINGS_PROBLEM_TRANSLATION_KEYS,
      subproblemTranslationKeys: SETTINGS_SUBPROBLEM_TRANSLATION_KEYS,
      fieldLabelKeys: AI_ASSISTANT_SETTINGS_FIELD_LABEL_KEYS,
    });
  }
}
